import React from 'react'

import { useScriptState } from '@/components/Event/NewScript/hooks/state/useScriptState'
import { useFormattedCurrentDateParts } from '@/components/Event/NewScript/hooks/useFormattedCurrentDateParts'
import {
  getEventTypeLongLabel,
  getEventTypeShortLabel,
  isExaminationUnderOath,
} from '@/components/Event/NewScript/utils/event'
import { AttendeeRole, DBFContact } from '@/constants'
import { Event, ReporterBase } from '@/types'
import { getStateIndefiniteArticle, getStateName } from '@/utils/states'
import { getEventTimezone } from '@/utils/time'

type OpeningVerbatimProps = {
  event: Event
  reporter?: ReporterBase | null
  isAK: boolean
  isAR: boolean
  isAZ: boolean
  isDE: boolean
  isDC: boolean
  isIL: boolean
  isKY: boolean
  isME: boolean
  isND: boolean
  isNE: boolean
  isNY: boolean
  isNV: boolean
  isPA: boolean
  isNM: boolean
  isMS: boolean
  isMT: boolean
  isRI: boolean
  isSD: boolean
  isTN: boolean
  isUT: boolean
  isVA: boolean
  isVT: boolean
  isWV: boolean
  isWY: boolean
  triggerProceedCopy?: boolean
}

export const OpeningVerbatim: React.FC<OpeningVerbatimProps> = ({
  event,
  reporter,
  isAK,
  isAR,
  isAZ,
  isDE,
  isDC,
  isIL,
  isKY,
  isME,
  isPA,
  isND,
  isNE,
  isNY,
  isNV,
  isNM,
  isMS,
  isMT,
  isRI,
  isSD,
  isTN,
  isUT,
  isVA,
  isVT,
  isWV,
  isWY,
  triggerProceedCopy,
}) => {
  const isEUO = isExaminationUnderOath(event)
  const dateTime = useFormattedCurrentDateParts(getEventTimezone(event))
  const caseNumber = event.case.number ?? '[ ]'
  const reporterCounty = reporter?.county ?? '[ ]'
  const reporterState = reporter?.state ? getStateName(reporter.state) : '[ ]'
  const name = reporter?.name ?? '[ ]'
  const { state } = useScriptState()
  const witness = state.attendees.find(
    attendee => attendee.role === AttendeeRole.WITNESS,
  )

  if (
    isAK ||
    isAR ||
    isAZ ||
    isDE ||
    isDC ||
    isIL ||
    isKY ||
    isME ||
    isND ||
    isNM ||
    isNY ||
    isMT ||
    isPA ||
    isRI ||
    isSD ||
    isTN ||
    isNV ||
    isUT ||
    isVA ||
    isVT ||
    isNE ||
    isWV ||
    isWY
  ) {
    return (
      <>
        Good {dateTime.partOfTheDay}, we are now on the record. Today is{' '}
        {dateTime.dayOfWeek}. It is {dateTime.formattedDate}, the time is{' '}
        {dateTime.formattedTimeWithTimezone}. We are present to record a remote{' '}
        {getEventTypeLongLabel(event)} in the case of {event.case.name}, court{' '}
        {isNY && isEUO ? 'index number' : 'case number'} {caseNumber}. My name
        is {name}, I will be the Digital Reporter for today’s{' '}
        {getEventTypeShortLabel(event)}. I am located in {reporterCounty},{' '}
        {reporterState} and I work with Filevine Depositions located in{' '}
        {DBFContact.ADDRESS}.{' '}
        {triggerProceedCopy && <>You may proceed, counsel.</>}
      </>
    )
  }

  if (isMS) {
    return (
      <>
        Good {dateTime.partOfTheDay}, we are now on the record. Today is{' '}
        {dateTime.dayOfWeek}. It is {dateTime.formattedDate}, the time is{' '}
        {dateTime.formattedTimeWithTimezone}. We are present to record the{' '}
        {getEventTypeLongLabel(event)}, in the case of {event.case.name}, court
        case number {caseNumber}. My name is {name},{' '}
        {getStateIndefiniteArticle(reporterState)} {reporterState} notary
        public, and I will be the Digital Reporter for today’s{' '}
        {getEventTypeShortLabel(event)}. I am located in {reporterCounty},{' '}
        {reporterState} and I work with Filevine Depositions located in{' '}
        {DBFContact.ADDRESS}.
      </>
    )
  }

  if (isEUO) {
    return (
      <>
        Good {dateTime.partOfTheDay}, we are now on the record. Today is{' '}
        {dateTime.dayOfWeek}. It is {dateTime.formattedDate}, the time is{' '}
        {dateTime.formattedTimeWithTimezone}. We are present to record the
        Examination Under Oath of {witness?.name ?? '<WITNESS>'}. My name is{' '}
        {name}, {getStateIndefiniteArticle(reporterState)} {reporterState}{' '}
        notary public, I will be the Digital Reporter for today’s proceedings.
      </>
    )
  }

  return (
    <>
      Good {dateTime.partOfTheDay}, we are now on the record. Today is{' '}
      {dateTime.dayOfWeek}. It is {dateTime.formattedDate}, the time is{' '}
      {dateTime.formattedTimeWithTimezone}. We are present to record{' '}
      {event.name} in the case of {event.case.name}, court case number{' '}
      {caseNumber}. My name is {name},{' '}
      {getStateIndefiniteArticle(reporterState)} {reporterState} notary public,
      and I will be the Digital Reporter for today’s{' '}
      {getEventTypeShortLabel(event)}.
    </>
  )
}
